import { useState } from 'react';
import { Link } from 'react-router';
import {
  BUTTON_CLASS,
  DataTable,
  Eyebrow,
  FileKindIcon,
  formatUtcDate,
  MetaRow,
  neutralButtonStyle,
  StatusDot,
  token,
  type DataTableColumn,
} from '@bp-files/ui';
import { RECYCLE_BIN_COPY } from '@/components/RecycleBin/recycleBin.copy';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { formatBytes } from '@/lib/formatBytes';
import { messageFrom } from '@bp-files/client';
import { useRecycleBin } from '@/lib/useRecycleBin';
import { purgeRecycleBin, restoreFile, type DeletedFile, type RecycleBinPurge } from '@/lib/api';

/**
 * The recycle bin — one agency's deletions, across every shared space (FIL-11).
 *
 * **A sibling of the space list, not a fifth surface and not a tab.** Files is a
 * three-level navigation — spaces → space → folder — so a tab bar above it would
 * be the wrong furniture, and the Files tab would have to stay lit three levels
 * down, which is not how `UsersSurface`'s tabs behave. Living at
 * `/files/recycle-bin` keeps `SurfaceNav`'s prefix matching working with no
 * change, and `surfaces.ts` at four entries.
 *
 * **It is not admin-only.** §5.3 gives a member "own deletions" for restore, so
 * the role shows up per row in `canRestore` and never on the route.
 *
 * **Three rules this encodes rather than decorates:**
 *
 * - The rows are rendered in the order the server sent. `deletedAt` is in no
 *   key, so the API sorts — and a second opinion here would be a client-side
 *   re-sort of an order that was already settled.
 * - **Restore is one click.** It takes nothing away, which is `MembersTab`'s
 *   line between revoke and reactivate. Do not add a confirm for symmetry with
 *   the folder view's delete.
 * - **`canRestore` is visibility, not authorization** (FIL-11a). The API applies
 *   the same rule to the request (SEC-5); a row that offers no button is a
 *   courtesy, not a boundary.
 *
 * There is no hash column and no kind column, deliberately: FIL-11 names
 * deleted-by, deleted-at, and days-remaining. This is not FIL-4's table, and a
 * kind badge would mean a second list of eight labels that can drift from
 * `FOLDER_COPY.kinds`. The name cell prefixes a kind icon the same way
 * FIL-4's does, though — `FileKindIcon` from `@bp-files/ui`, labelled from
 * `FOLDER_COPY.kinds` directly so there is still only the one list.
 */

export function RecycleBin() {
  const { state, retry, refresh } = useRecycleBin();
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  // Two stages, `FolderView`'s folder-delete pattern — and here the confirm is
  // not decoration. Restore takes nothing away and is one click (above); this
  // destroys evidence with no undo, which is the one thing the thirty days
  // existed to prevent (FIL-11b).
  const [confirmingPurge, setConfirmingPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<RecycleBinPurge | undefined>(undefined);

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-muted') }}>
        {RECYCLE_BIN_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    return <RecycleBinError status={state.status} onRetry={retry} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Eyebrow>{RECYCLE_BIN_COPY.eyebrow}</Eyebrow>
        <h1 className="text-xl font-semibold" style={{ color: token('bp-text') }}>
          {RECYCLE_BIN_COPY.title}
        </h1>
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {RECYCLE_BIN_COPY.summary}
        </p>
      </div>

      {state.truncated ? (
        // `status`, never `alert`: nothing failed. The rows on screen are all
        // real — there are simply more than one read carries (ADR-0013).
        <p role="status" className="text-xs" style={{ color: token('bp-text-muted') }}>
          {RECYCLE_BIN_COPY.states.truncated}
        </p>
      ) : null}

      {actionError === undefined ? null : (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {actionError}
        </p>
      )}

      {purgeResult === undefined ? null : (
        // `status`, not `alert`: the act succeeded. It is announced at all
        // because the rows it names are gone from the table below, so the count
        // is the only evidence left that anything happened.
        <p role="status" className="flex flex-col gap-1 text-sm">
          <span style={{ color: token('bp-text') }}>
            {RECYCLE_BIN_COPY.purge.done(purgeResult.purged)}
          </span>
          {purgeResult.remaining === 0 ? null : (
            // The honest half (FIL-11a). A member who emptied their own
            // deletions and still sees rows needs to know why.
            <span style={{ color: token('bp-text-muted') }}>
              {RECYCLE_BIN_COPY.purge.remaining(purgeResult.remaining)}
            </span>
          )}
        </p>
      )}

      {/*
        Offered only when there is something to destroy, and only when some of
        it is this caller's to destroy. A bin holding nothing but an
        administrator's deletions gives a member a button whose only outcome is
        `purged: 0` — which teaches them to ignore the control, and is the same
        argument `AgencyActsMenu` makes for gating an act on the state that
        would refuse it.
      */}
      {state.files.some((file) => file.canRestore) ? (
        <div className="flex flex-col items-start gap-2">
          {confirmingPurge ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium" style={{ color: token('bp-text') }}>
                {RECYCLE_BIN_COPY.purge.confirm.title}
              </p>
              <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
                {RECYCLE_BIN_COPY.purge.confirm.body(
                  state.files.filter((file) => file.canRestore).length,
                )}
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingPurge(false);
                  }}
                  className={BUTTON_CLASS}
                  style={neutralButtonStyle()}
                >
                  {RECYCLE_BIN_COPY.purge.confirm.cancel}
                </button>

                <button
                  type="button"
                  disabled={purging}
                  onClick={() => void emptyBin()}
                  className={BUTTON_CLASS}
                  // `RowActions`' confirm treatment: neutral outline, red
                  // label, so the destructive act does not out-weigh Keep them.
                  style={{ ...neutralButtonStyle(), color: token('bp-danger') }}
                >
                  {purging ? RECYCLE_BIN_COPY.purge.working : RECYCLE_BIN_COPY.purge.confirm.submit}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setPurgeResult(undefined);
                setConfirmingPurge(true);
              }}
              className={BUTTON_CLASS}
              style={neutralButtonStyle()}
            >
              {RECYCLE_BIN_COPY.purge.label}
            </button>
          )}
        </div>
      ) : null}

      <BinTable files={state.files} busyId={busyId} onRestore={(id) => void putBack(id)} />
    </div>
  );

  async function emptyBin() {
    setPurging(true);
    setActionError(undefined);

    try {
      const result = await purgeRecycleBin();
      setPurgeResult(result);
      setConfirmingPurge(false);
      // Refetch rather than empty the list here: what a member could not purge
      // stays, and the server is the only thing that knows which rows those are.
      refresh();
    } catch (caught) {
      setActionError(messageFrom(caught, RECYCLE_BIN_COPY.purge.failed));
    } finally {
      setPurging(false);
    }
  }

  async function putBack(id: string) {
    setBusyId(id);
    setActionError(undefined);

    try {
      await restoreFile(id);
      // Refetch rather than splice: the restored row has to leave, and if the
      // call lost a race the list is the only thing that knows what is true.
      refresh();
    } catch (caught) {
      // The API's own message. A 403 names the rule — restore keys on who
      // deleted a file, not who uploaded it — and a 409 says it is already back.
      setActionError(messageFrom(caught, RECYCLE_BIN_COPY.restore.failed));
    } finally {
      setBusyId(undefined);
    }
  }
}

/**
 * The refusals.
 *
 * **There is no 404 case, and its absence is the point**: this route takes no
 * id, so nothing about it can be absent. A 403 is permanent and offers no retry.
 */
function RecycleBinError({ status, onRetry }: { status: number; onRetry: () => void }) {
  const message =
    status === 403 ? RECYCLE_BIN_COPY.states.forbidden : RECYCLE_BIN_COPY.states.error;

  return (
    <div className="flex flex-col items-start gap-3">
      <p role="alert" className="flex items-center gap-2 text-sm text-danger">
        <StatusDot tone="alert" />
        {message}
      </p>

      {status === 403 ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
          style={neutralButtonStyle()}
        >
          {RECYCLE_BIN_COPY.states.retry}
        </button>
      )}
    </div>
  );
}

function BinTable({
  files,
  busyId,
  onRestore,
}: {
  files: DeletedFile[];
  busyId: string | undefined;
  onRestore: (id: string) => void;
}) {
  // Sorting is off for `FolderView`'s reason turned one way further: the server
  // owns most-recently-deleted-first, and a person reading a bin is looking for
  // something they just lost — re-sorting by name would bury the row they came
  // for.
  const columns: DataTableColumn<DeletedFile>[] = [
    {
      id: 'name',
      header: RECYCLE_BIN_COPY.columns.name,
      cell: (file) => (
        <div className="flex items-center gap-2">
          <FileKindIcon
            kind={file.kind}
            label={FOLDER_COPY.kinds[file.kind]}
            className="shrink-0"
          />
          <span style={{ color: token('bp-text') }} className="font-medium">
            {file.name}
          </span>
        </div>
      ),
    },
    {
      id: 'size',
      header: RECYCLE_BIN_COPY.columns.size,
      align: 'end',
      // Binned files still occupy paid storage until the purge job runs (STO-3),
      // so the size is not decoration here.
      cell: (file) => (
        <span style={{ color: token('bp-text-muted'), fontVariantNumeric: 'tabular-nums' }}>
          {formatBytes(file.sizeBytes, RECYCLE_BIN_COPY.units)}
        </span>
      ),
    },
    {
      id: 'origin',
      header: RECYCLE_BIN_COPY.columns.origin,
      cell: (file) => (
        <Link
          to={`/files/spaces/${encodeURIComponent(file.origin.spaceId)}/folders/${encodeURIComponent(file.origin.folderId)}`}
          // Named per row: the visible text is where it goes, but several rows
          // can share a folder and a screen reader hears the label alone.
          aria-label={RECYCLE_BIN_COPY.originLink(file.origin.folderName)}
          className="text-xs transition-colors hover:underline"
          style={{ color: token('bp-text-muted') }}
        >
          {RECYCLE_BIN_COPY.origin(file.origin.folderName, file.origin.spaceName)}
        </Link>
      ),
    },
    {
      id: 'deletedBy',
      header: RECYCLE_BIN_COPY.columns.deletedBy,
      // A name and no agency: PERM-4 makes it always the reader's own (FIL-11).
      cell: (file) => <span style={{ color: token('bp-text-muted') }}>{file.deletedBy}</span>,
    },
    {
      id: 'deletedAt',
      header: RECYCLE_BIN_COPY.columns.deletedAt,
      // MetaRow owns the amber-for-time rule, so the treatment is right without
      // this component remembering it.
      cell: (file) => (
        <MetaRow
          time={formatUtcDate(file.deletedAt, RECYCLE_BIN_COPY.dateLocale)}
          dateTime={file.deletedAt}
        />
      ),
    },
    {
      id: 'timeLeft',
      header: RECYCLE_BIN_COPY.columns.timeLeft,
      cell: (file) => <TimeLeft days={file.daysRemaining} />,
    },
    {
      id: 'actions',
      header: RECYCLE_BIN_COPY.columns.actions,
      align: 'end',
      cell: (file) =>
        file.canRestore ? (
          <RestoreButton file={file} busy={busyId === file.id} onRestore={onRestore} />
        ) : // Nothing, deliberately. The Deleted by column already names why this
        // row is inert — restore keys on the deleter (§5.3) — and a disabled
        // button with no explanation would only raise the question.
        null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={files}
      rowKey={(file) => file.id}
      caption={RECYCLE_BIN_COPY.states.tableCaption}
      emptyState={RECYCLE_BIN_COPY.states.empty}
    />
  );
}

/**
 * How long is left (UPL-10).
 *
 * Zero is worded as an event rather than counted, because that is what it is:
 * purge is a job and not a clock, so a file past its window is `Deleted` and
 * waiting for the next sweep. "0 days" would read as though it had already gone.
 */
function TimeLeft({ days }: { days: number }) {
  if (days === 0) {
    return (
      <span className="flex items-center gap-1 text-xs" style={{ color: token('bp-danger') }}>
        <StatusDot tone="alert" size="sm" />
        {RECYCLE_BIN_COPY.purgesNext}
      </span>
    );
  }

  return (
    <span className="text-xs" style={{ color: token('bp-text-muted') }}>
      {RECYCLE_BIN_COPY.daysLeft(days)}
    </span>
  );
}

/**
 * FIL-11: restore, in one click.
 *
 * **No confirmation, and that is the rule rather than an omission.** This
 * workspace confirms an action that takes something away — delete, revoke,
 * sever — and does not confirm one that gives something back. Restore is the
 * second kind.
 */
function RestoreButton({
  file,
  busy,
  onRestore,
}: {
  file: DeletedFile;
  busy: boolean;
  onRestore: (id: string) => void;
}) {
  return (
    <button
      type="button"
      // Named per row: several buttons reading "Restore" are indistinguishable
      // to a screen reader, and the row context that separates them is visual.
      aria-label={RECYCLE_BIN_COPY.restore.action(file.name)}
      disabled={busy}
      onClick={() => {
        onRestore(file.id);
      }}
      className={BUTTON_CLASS}
      style={neutralButtonStyle()}
    >
      {busy ? RECYCLE_BIN_COPY.restore.working : RECYCLE_BIN_COPY.restore.label}
    </button>
  );
}

/**
 * The way in, rendered in the Files surface header beside Connect an agency.
 *
 * A component rather than a copy import in `App.tsx`, which keeps that file
 * composing components exactly as it already does for `SpaceList`.
 *
 * **A compact button now, not a full-width card.** The card's own count and
 * countdown sentence does not fit a header slot sized to sit beside a
 * one-line "Connect an agency" — so this drops both. Sitting beside a
 * plain-text button, a count badge made this one visibly taller and wider
 * than its neighbour; someone who needs to know *how many* or *how soon*
 * still has the bin's own table for that, and this button's job is only to
 * say there is somewhere to go.
 *
 * **Renders nothing unless it has something worth saying.** `FilesNav` gives
 * the bin a standing sidebar entry now, always reachable regardless of this
 * button — so an empty bin renders nothing here rather than an inert
 * "Recycle bin" button beside Connect that duplicates the sidebar for no
 * reason. Loading and an error both render null for the same reason: the
 * sidebar link is already the way in for either state.
 */
export function RecycleBinButton() {
  const { state } = useRecycleBin();

  if (state.kind !== 'loaded' || state.files.length === 0) return null;

  return (
    <Link
      to="/files/recycle-bin"
      className="inline-flex min-h-9 shrink-0 items-center justify-center border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-elevated"
      style={{
        color: token('bp-text'),
        borderColor: token('bp-border-strong'),
        borderRadius: token('radius-md'),
      }}
    >
      {RECYCLE_BIN_COPY.entry.label}
    </Link>
  );
}
